import * as React from 'react';
import { ArrowRight, IdCard } from 'lucide-react';

import { Badge } from 'components/ui/badge';
import { cardVariants } from 'components/ui/card';
import { cn } from 'lib/utils';

/**
 * Puerto real de `VerifyIdentityCard.tsx` (chirigota, `src/components/MiBeneficioV2/`,
 * usado por `IndividualDashboardV2.tsx`): el card único de activación del plan del
 * flujo de "aterrizaje unificado" (sept. 2026). La identidad va con el USUARIO, no
 * con el traspaso: se pinta mientras la identidad no está verificada, tanto si el
 * usuario viene de un traspaso como de un alta nueva. El traspaso, si lo hay, se
 * tramita en paralelo y vive en su propia card colapsable (`ui/TransferStatusCard`),
 * nunca aquí.
 *
 * REGLA DE NEGOCIO: el traspaso NUNCA se da por válido sin identidad verificada,
 * aunque el dinero llegue antes: no se puede aportar ni ver el plan activo hasta
 * completar este paso. Son dos procesos independientes, y la identidad es la que de
 * verdad desbloquea.
 *
 * UNA SOLA card para las TRES "operaciones de gran interés" de aterrizaje unificado
 * (chirigota, `src/components/MiBeneficioV2/`), misma estética exacta en las tres:
 * solo cambia el icono y el copy. `VerifyIdentityCard` (identidad, `icon` por
 * defecto), `TransferSignatureCard` (firma de traspaso, pasar
 * `icon={<FileSignature .../>}`) y `VerifyTransferDestinationCard` (confirmar plan de
 * destino, `icon={<Target .../>}`). Mutuamente excluyentes por diseño, nunca dos a la
 * vez: identidad tiene siempre la prioridad más alta; entre firma y destino no hay un
 * orden fijo, se muestra la que esté pendiente más recientemente. No había que
 * triplicar el componente, solo parametrizar el icono. Ver `card-action.stories.tsx`
 * para las 3 variantes reales, y `Screens/Resumen Multicuenta` para la pantalla que
 * de verdad las usa.
 *
 * 🔴 Llevaba además `benefits`/`blockedItems`/`disclaimer` (cajas internas con
 * bullets + un aviso de cierre) — quitados del todo (6 oct 2026, construyendo
 * `Screens/Resumen Multicuenta`): repetían en una caja aparte lo que la propia
 * `description` ya dice en prosa, y Elena los calificó de "texto poco relevante" al
 * verlos montados de verdad en la pantalla. Ninguna de las 3 variantes reales los usa
 * ya, así que no se quedan como una capacidad muerta del componente.
 */
export interface CardActionProps {
	/** Por defecto `IdCard` (identidad). Pasar `FileSignature`/`Target` (u otro) para firma/destino. */
	icon?: React.ReactNode;
	title?: string;
	/**
	 * Badge junto al título. Con una acción, a qué plan pertenece la operación: solo en firma y
	 * destino (la identidad es del usuario, no de un plan), y necesario en pantallas multi-plan
	 * (Resumen), donde esta card vive encima de TODOS los planes y sin él no se sabría a cuál se
	 * refiere. Con `informative`, el estado («En revisión», «Necesita tu ayuda»).
	 */
	planLabel?: string;
	description?: string;
	ctaLabel?: string;
	/** Cuánto lleva completar la acción — ej. "2 min", "menos de 1 min". Se pinta junto a `ctaLabel` en la franja inferior. */
	ctaTimeLabel?: string;
	onVerify?: () => void;
	/**
	 * Solo informa: identidad en revisión o rechazada, donde el usuario no puede hacer nada. La card
	 * deja de ser un `<button>` (no se enfoca ni se pulsa) y no lleva la franja de acción; `ctaLabel`,
	 * `ctaTimeLabel` y `onVerify` se ignoran. En chirigota se quitó el botón de estos estados el
	 * 30/09/2026: cada clic volvía a abrir el KYC y metía un `kyc-start` falso en el embudo B2C.
	 */
	informative?: boolean;
	/**
	 * Solo con `informative`, debajo de la descripción: lo que el usuario sí puede hacer, como
	 * escribir a soporte con un enlace `mailto:`. Con acción se ignora: la card entera es un
	 * `<button>` y un enlace dentro sería HTML inválido.
	 */
	children?: React.ReactNode;
	className?: string;
}

/** Lo que ocupa el icono más el hueco hasta el título: 24 px (el icono más grande) + 8 px. */
const ICON_SLOT = 32;

export const CardAction = ({
	// `w-6 h-6` y no `w-5 h-5` como los demás: el dibujo del IdCard es apaisado (20×14 en su rejilla de 24)
	// y con la misma caja se veía claramente más pequeño que FileSignature/Target (20×20 y 18×20).
	icon = <IdCard className="w-6 h-6 text-warning" strokeWidth={2} />,
	title = 'Verifica tu identidad para activar el plan',
	planLabel,
	description = 'Es un requisito legal del plan de pensiones. Hasta que la completes no podemos aceptar aportaciones, aunque el traspaso llegue antes.',
	ctaLabel = 'Verificar mi identidad',
	ctaTimeLabel = '2 min',
	onVerify,
	informative = false,
	children,
	className
}: CardActionProps) => {
	// El icono solo se muestra si el encabezado (título + badge) cabe en UNA línea a su lado. Si pasara a
	// dos, se oculta. CSS no puede saber si un texto va a saltar de línea, así que se mide: el ancho
	// natural del título y el badge en una sola línea (`medidaRef`, invisible) contra el ancho de la
	// fila menos el hueco del icono. La medida NO depende de si el icono está o no, así que no hay
	// parpadeo (si dependiera, ocultarlo haría que cupiera, se volvería a mostrar y no cabría...).
	const filaRef = React.useRef<HTMLDivElement>(null);
	const medidaRef = React.useRef<HTMLDivElement>(null);
	const [mostrarIcono, setMostrarIcono] = React.useState(true);
	React.useLayoutEffect(() => {
		const fila = filaRef.current;
		const medida = medidaRef.current;
		if (!fila || !medida) return;
		const comprobar = () => setMostrarIcono(medida.offsetWidth <= fila.clientWidth - ICON_SLOT);
		comprobar();
		const observer = new ResizeObserver(comprobar);
		observer.observe(fila);
		// Las fuentes cambian el ancho del texto al cargar.
		void document.fonts?.ready.then(comprobar);
		return () => observer.disconnect();
	}, [title, planLabel]);

	// Informativa: un `<div>` y no un `<button>` deshabilitado, que seguiría anunciándose como botón.
	const Raiz = informative ? 'div' : 'button';

	return (
		<Raiz
			{...(informative ? {} : { type: 'button' as const, onClick: onVerify })}
			className={cn(
				cardVariants({ variant: 'elevated' }),
				// overflow-hidden: para que la franja naranja de abajo recorte a las mismas
				// esquinas redondeadas de la card (si no, su fondo sale a lo cuadrado por fuera).
				'w-full flex flex-col overflow-hidden text-left',
				// Toda la card es el control, no solo la franja de abajo — área táctil generosa
				// (Foundations/Accessibility, 24×24 mínimo) y un único foco en vez de dos.
				!informative &&
					'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
				className
			)}
		>
			<div className="p-4 flex flex-col gap-1">
				<div ref={filaRef} className="relative flex items-start gap-2">
					{/* Icono pequeño EN LÍNEA con el título, no en una columna aparte a la izquierda: así no hay
				    sangría que desalinee nada (la franja de abajo, el título y la descripción arrancan
				    todos en el mismo borde) y la descripción usa todo el ancho. El icono lo dimensiona
				    quien lo pasa (20-24 px: el IdCard por defecto va a 24 porque su dibujo es apaisado) y se alinea con la PRIMERA línea del título: la fila es fija (no
				    `flex-wrap`), si no, con un título largo el icono se quedaba solo en una línea. Solo se
				    muestra si el encabezado cabe en una línea (ver `medidaRef`); si pasa a dos, se oculta. */}
					{mostrarIcono && (
						<span className="flex h-[1.375rem] shrink-0 items-center lg:h-[1.55rem]">{icon}</span>
					)}
					<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
						{/* `<span>`, no `<h2>`: dentro de un `<button>` real el contenido debe ser
				    de fraseo, sin elementos de encabezado — mismo peso visual, sin el landmark. */}
						<span className="text-base lg:text-lg font-semibold font-heading text-foreground leading-snug">
							{title}
						</span>
						{/* Badge, no un <p> más en la pila: era la tercera línea de texto plano
				    seguida (título/planLabel/descripción) sin ninguna jerarquía entre ellas
				    — junto al título se lee de un vistazo que es metadato ("a qué plan"),
				    no una frase más que leer en orden. */}
						{planLabel && (
							<Badge variant="outline" className="shrink-0 font-normal">
								{planLabel}
							</Badge>
						)}
					</div>
					{/* Medida del encabezado en una sola línea (ver `medidaRef`): invisible y fuera del flujo. */}
					<div
						ref={medidaRef}
						aria-hidden="true"
						className="pointer-events-none invisible absolute left-0 top-0 inline-flex items-center gap-x-2 whitespace-nowrap"
					>
						<span className="text-base lg:text-lg font-semibold font-heading leading-snug">
							{title}
						</span>
						{planLabel && (
							<Badge variant="outline" className="font-normal">
								{planLabel}
							</Badge>
						)}
					</div>
				</div>
				<p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
				{informative && children}
			</div>

			{/* Franja de cierre, no un botón pequeño dentro de la card: a todo el ancho, con el
		    mismo naranja de aviso que el icono de arriba (`--warning-surface`/`--warning-text`,
		    el par ya verificado por contraste en ui/Badge) — se lee como el paso siguiente de
		    la card, no como un elemento flotando dentro de ella. */}
			{!informative && (
				<div className="mt-auto flex items-center justify-between gap-3 bg-warning-surface px-4 py-3 text-warning-text">
					{/* Dos líneas fijas, no una sola concatenada con "·": a 320-360px las 3 variantes
			    reales (no casos de prueba inventados) envolvían la franja entera a 2-3 líneas,
			    inflando su alto y descolocando la flecha. Apilado siempre, nunca envuelve más
			    de 2 líneas en ningún ancho — sin inventar un breakpoint nuevo. */}
					<span className="min-w-0">
						<span className="block text-sm font-medium">{ctaLabel}</span>
						{/* text-warning-text a opacidad completa, no /80: al 80% el contraste real baja
				    a 4.01:1 (axe lo cazó) — por debajo del 4.5:1 de AA. El propio token ya está
				    medido para pasar (6.29:1) solo a opacidad 1. */}
						{ctaTimeLabel && (
							<span className="block text-xs text-warning-text">{ctaTimeLabel}</span>
						)}
					</span>
					<ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
				</div>
			)}
		</Raiz>
	);
};
